/******************************************************************************
 *                                                                            *
 *  Copyright (c) 2018, 2025 by AsterionDB Inc.                               *
 *                                                                            *
 *  All rights reserved.  No part of this work may be reproduced or otherwise *
 *  incorporated into other works without the express written consent of      *
 *  AsterionDB Inc.                                                           *
 *                                                                            *
 *****************************************************************************/

import React from 'react'

import { JsonView } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';

import { Row, Col, Nav, NavLink } from 'reactstrap';

import "video-react/dist/video-react.css";
import { Player, ControlBar } from 'video-react';

import PasswordModal from './PasswordModal'

import {AppContext} from '../AppContext';

import axios from 'axios';

class SecureWeblink extends React.Component 
{
  weblinkToken = '';

  async closePasswordModal(accepted, password)
  {
    if (!accepted)
    {
      this.setState({passwordModalIsOpen: false});
      return;
    }

    let bodyData = {};
    bodyData.weblinkToken = this.weblinkToken;
    bodyData.weblinkPassword = password;

    let result = await this.context.dbTwig.callRestAPI('dgBunker', 'validateSecureWeblinkPassword', bodyData);
    if ('success' !== result.status) return this.context.dbTwig.apiErrorHandler(result, 'Error accessing this secure link')
    if ('ReactJson' === result.jsonData.htmlElement && 'STREAM' === result.jsonData.contentDisposition) this.getObjectData(result.jsonData.secureStream);
    this.setState({passwordModalIsOpen: false, weblinkInfo: result.jsonData, weblinkStatus: 'accessGranted'});
    this.context.postNotification('info', 'Access granted.');
  }

  constructor(props)
  {
    super();

    this.state =
    {
      greetingPhrase: '',
      passwordModalIsOpen: false,
      weblinkStatus: undefined,
      weblinkInfo: undefined,
      objectData: undefined
    }

    this.closePasswordModal = this.closePasswordModal.bind(this);
    this.getObjectDataResponse = this.getObjectDataResponse.bind(this);
  }

  componentDidMount()
  {
    // because we only expect a single argument,
    // instead of parsing multiple query arguments, 
    // we can actually treat this as a security
    // enhancement and ignore requests that aren't
    // what we expect
    if (window.location.search.substring(0, 1) !== '?'
     || /[^0-9a-zA-Z]/.test(window.location.search.substring(1)))
    {
      // redirect back to login page
      // to external URI:
      // window.location.href = this.redirectURI;
      // can use history.push() if you want the current page to stay in the browser history
      this.setState({weblinkStatus: false});
      return;
    }

    // by taking the entire query string without checking, if the user
    // adds anything funny, they will just invalidate their request
    // (as long as the DB passes this entire string to its decrypt function)

    this.weblinkToken = window.location.search.substring(1);
    this.fetchSecureLinkInfo();
  }

  componentWillUnmount()
  {
    if (undefined !== this.refs.player && !this.refs.player.paused) this.refs.player.pause();
  }

  async fetchSecureLinkInfo()
  {
    let bodyData = {weblinkToken: this.weblinkToken};
    let result = await this.context.dbTwig.callRestAPI('dgBunker', 'getSecureWeblinkInfo', bodyData);
    if ('success' !== result.status) return this.context.dbTwig.apiErrorHandler(result, 'Error getting secure weblink info');
    this.setState(
    {
      passwordModalIsOpen:  result.jsonData.passwordRequired === 'N' ? false : true,
      weblinkStatus:  result.jsonData.passwordRequired === 'Y' ? 'passwordRequired' : 'accessGranted', 
      weblinkInfo:  result.jsonData
    });
    if ('ReactJson' === result.jsonData.htmlElement) await this.getObjectData(result.jsonData.secureStream);
  }

  getObjectDataResponse(response)
  {
    this.setState({objectData: response.data});
  }

  async getObjectData(secureWeblink)
  {
    axios.get(secureWeblink)
      .then(this.getObjectDataResponse)
      .catch(function (error) 
      {
        // handle error
        return this.context.dbTwig.apiErrorHandler(error, 'Error getting object data.');
      })
      .finally(function () 
      {
        // always executed
      });
  }

  renderStream()
  {
    let objectHeight = window.outerHeight * .6;

    var element = null;
    switch (this.state.weblinkInfo.htmlElement)
    {
      case null:
        element = <div style={{backgroundColor: '#cccccc', textAlign: 'center'}}>No Player/Viewer Available</div>;
        break;

      case 'ReactJson':
        if (undefined === this.state.objectData) return null;
        element = <JsonView data={this.state.objectData}/>;
        break;

      case 'img':
        element = 
          <div>
            <img style={{maxWidth: '100%', paddingTop: '10px', paddingBottom: '10px', marginLeft: 'auto', marginRight: 'auto', display: 'block'}} 
              src={this.state.weblinkInfo.secureStream} alt="Object view" 
            />
          </div>;
        break;

      case 'object':
        element =
          <div>
            <object style={{height: objectHeight, width: '100%'}} type={this.state.weblinkInfo.mimeType} data={this.state.weblinkInfo.secureStream}>
                AsterionDB Generic Viewer
            </object>
          </div>;
        break;

      case 'Player':
        element =
          <div>          
            <Player ref='player' props={{id: 'player'}} className="pl-4 pr-4 mx-auto" muted={false} autoPlay={false} fluid={true} 
                src={this.state.weblinkInfo.secureStream} >
              <ControlBar autoHide={true} />
            </Player>
          </div>;
        break;

      default:
        break;
    }

    return element;
  }

  render() 
  {
    switch (this.state.weblinkStatus)
    {
      case undefined:
        return false;

      case 'invalid':
        return(
          <div>
            This weblink is not valid.
          </div>
        );

      case 'passwordRequired':
        return(
          <div>
            <PasswordModal closeModal={this.closePasswordModal} modalIsOpen={this.state.passwordModalIsOpen} 
              modalHeader="Secure Weblink Password" 
              modalBody="A password is required to access this secure content."
            />
            Welcome to {this.state.weblinkInfo.firstName}&nbsp;{this.state.weblinkInfo.lastName}'s secure content web-page.
          </div>
        );

      case 'accessGranted':
        if ('STREAM' === this.state.weblinkInfo.contentDisposition) return this.renderStream();

        if ('DOWNLOAD' === this.state.weblinkInfo.contentDisposition)
        {
          return(
            <div>
              <Row>
                <Col sm='4'>Access to secure content granted.  Click on this link to begin the download.</Col>
              </Row>
              <Row>
                <Col sm='4'>
                  <Nav>
                    <NavLink href={this.state.weblinkInfo.secureStream}>{this.state.weblinkInfo.objectName}.{this.state.weblinkInfo.fileExtension}</NavLink>
                  </Nav>
                </Col>
              </Row>
              
            </div>
          );
        }

        return(false);

      default:
        return(false);
    }
  }
}

SecureWeblink.contextType = AppContext;

export default SecureWeblink